import getRandomUUID from '../../generic/utilities/randomuuid/RandomUUID'
import { isTechnologyExternal } from './jobExternalCharacter'
import { isTechnologyManagingPreparation } from './jobPreparationCharacter'
import { TOLERATED_JOB_FIELDS } from './operationLinkIntegrity'
import { updatedOperationFeatureGroups } from './feature/featureGroupRevisionControl'

/**
 * Builds the jobReference of a JOB node that has just been dropped on the canvas from the palette.
 *
 * Extracted out of `WorkflowFlow#onDrop` so the inheritance rule below is verifiable on its own.
 *
 * S40 — INHERITANCE: a node whose technology is marked as subcontracted is born with the mark on. This
 * is a DEFAULT AT CREATION and this factory is the only place that applies it. Nothing recalculates it
 * afterwards: changing the technology of an existing node leaves it exactly as the configurator left it,
 * in either direction (S42), because the mark is a declaration of the configurator and not derived
 * data. The flag arrives with the palette object (`technologiesWorkflow` -> TechnologyBasicData).
 *
 * @param {Object} params
 * @param {string} params.technologyName Name of the technology dropped on the canvas
 * @param {Array<{name: string, external?: boolean, progressCalculationType?: Object}>} [params.technologies] Palette catalogue
 * @param {Array<{name: string}>} [params.indexValues]
 * @param {Array<Object>} [params.progressCalculationTypes]
 * @returns {Object} The new jobReference, not yet persisted
 */
export const createNewJobReference = ({
  technologyName,
  technologies = [],
  indexValues = [],
  progressCalculationTypes = []
}) => {
  const technologyObj = technologies.find((technology) => technology.name === technologyName)
  const bornSubcontracted = isTechnologyExternal(technologyObj)
  return {
    technology: technologyObj,
    external: bornSubcontracted,
    // Story 113 (S99/S100, C2/RN14): INHERITANCE AT CREATION, and only here. A node dropped from a
    // technology that manages preparation is born with the mark on; a technology with no readable
    // value produces an unmarked node. Nothing recalculates it afterwards -- changing the technology
    // of an existing node leaves it as the configurator left it (D3/S101), because the mark is a
    // declaration, not derived data. This factory is the seed's ONLY legitimate caller (S106).
    managePreparation: isTechnologyManagingPreparation(technologyObj),
    // Story 183 (S24/RN1): NO inheritance here. The night mark is a declaration of the planner, never
    // seeded by the technology (whatever its C2 says): a new job is a day job, and the field is explicit
    // because every save sends it (C1).
    nightJob: false,
    jobReferenceResults: [],
    jobIsFirt: false,
    rework: false,
    indexValue: indexValues.find((index) => index.name === 'DRAFT'),
    taskReferences: [
      {
        name: 'Task_1',
        // Story 124 (S48): a node born subcontracted is born with the SAME zeros that ticking the
        // mark by hand applies through the confirmation (fix #2885). Everything else on this object
        // is already zero at birth -- operatorQuantity, resources, preferenceDevice, jobPreparation --
        // so this duration was the only value that made a seeded job differ from a hand-marked one.
        // The seed never passes through that confirmation, so the zero has to be applied here.
        theoreticalTime: bornSubcontracted ? '00:00:00' : '00:00:01',
        // Story 208 (S41): born without machine time ("no data" is null at the API boundary, not a zero).
        deviceTime: null,
        taskOrder: 1,
        features: [],
        defaultProgram: null,
        taskToolInfos: [],
        uuid: getRandomUUID()
      }
    ],
    resources: [],
    preferenceDevice: null,
    fixtureReference: null,
    operatorQuantity: 0,
    remark: null,
    minimumQuantity: 1,
    jobPreparation: {
      initialPreparation: { time: '00:00:00', operators: '' },
      intermediatePreparation: { time: '00:00:00', operators: '' }
    },
    // Story 80 (S40): a new job is born with no return timing declared. Explicit null rather than
    // absent so the field travels in the payload and the diff sees it, the same reason jobPreparation
    // is initialised above.
    externalReturn: null,
    progressCalculationTypeDTO: technologyObj?.progressCalculationType ?? progressCalculationTypes[0]
  }
}

/**
 * Historia 130 (S42, ajuste del PO 2026-09-15): el job de una versión de biblioteca, preparado para
 * vivir como job PROPIO de un nodo del workflow.
 *
 * Lo que llega de N3 son las filas DE LA BIBLIOTECA, con sus ids. Mandarlas tal cual en el guardado
 * hace que el nodo apunte al job de la biblioteca en vez de tener el suyo, que es justo lo que la
 * historia no quiere (el vínculo es lógico, la copia es física: 03-dominio.md D-01 b). Aquí se
 * limpian los ids de persistencia y se renuevan los uuid, de modo que Sun cree filas nuevas para el
 * nodo y el vínculo se resuelva por `operationLink`, no por compartir filas.
 *
 * @param {Object} jobReference El `jobReference` de la versión, tal y como lo devuelve N3
 * @returns {Object} Una copia sin identidad persistida
 */
export const asDroppedOperationJob = (jobReference = {}) => {
  const copy = { ...jobReference, id: null }
  copy.taskReferences = (jobReference.taskReferences || []).map((task) => ({
    ...task,
    id: null,
    uuid: getRandomUUID(),
    features: (task.features || []).map((feature) => ({ ...feature, id: null })),
    taskToolInfos: (task.taskToolInfos || []).map((toolInfo) => ({ ...toolInfo, id: null }))
  }))
  copy.resources = (jobReference.resources || []).map((resource) => ({ ...resource, id: null }))
  if (jobReference.jobPreparation) {
    copy.jobPreparation = { ...jobReference.jobPreparation, id: null }
  }
  return copy
}

/**
 * Historia 152 (S15/S16 · `RN-01.8`, `RN-01.8b`, intención `COPY` de §8.3): el job con el que nace
 * un nodo arrastrado como COPIA LOCAL de un job de otro proceso **cuando su origen no se puede
 * leer**.
 *
 * Desde la **Enmienda 2** (PO, 22/09/2026) el camino normal es `asCopiedJobPreview`, que enseña el
 * job de origen; esto es la DEGRADACIÓN: si `GET workflow/{sourceWorkflowId}` falla o el nodo ya no
 * está, el drop no se bloquea y el nodo nace igual que antes de la enmienda.
 *
 * Es deliberadamente un **hueco**, no un job compuesto en cliente. La copia la hace **Sun** dentro
 * del guardado, por el mismo camino que `BIND`: el cliente no puede componerla porque los adjuntos
 * del job y de sus tareas no viajan en el `JobReference` (S16), y una copia sin sus planos y en
 * silencio es un defecto de esta especificación. Lo único que hace falta es que el nodo llegue con
 * UN job creado, porque `copyWorkflowJobIntoWorkflowJob` copia SOBRE él ("nothing of the target's
 * own content survives": borra sus tareas, sus documentos y sus recursos antes de copiar).
 *
 * Qué job se copia lo dicen `sourceNodeId` y `sourceWorkflowId` del `operationLink`, que son
 * parámetros TRANSITORIOS de la copia y no datos de origen (D11-bis).
 *
 * @param {Object} params
 * @param {Array<{name: string}>} [params.indexValues]
 * @param {Array<Object>} [params.progressCalculationTypes]
 * @returns {Object} El job mínimo del nodo recién soltado, que Sun sobrescribirá al guardar
 */
export const asCopiedJobPlaceholder = ({ indexValues = [], progressCalculationTypes = [] } = {}) => ({
  ...createNewJobReference({ technologyName: '', technologies: [], indexValues, progressCalculationTypes }),
  // La tecnología es la del job de origen y llega con la copia; hasta entonces el nodo no la
  // conoce (el árbol es una proyección ligera y no la trae, §8.4). `{}` y no `null`/`undefined`:
  // el lienzo lee `technology.name` para rotular la tarjeta.
  technology: {}
})

/** El tipo de recurso que es asignación de BOM DEL WORKFLOW, y por tanto del nodo, no de la operación. */
const WORKFLOW_RESOURCE = 'WORKFLOW_RESOURCE'

const resourceTypeOf = (resource) => resource?.resourceType?.name || resource?.resourceType || resource?.type || ''

/**
 * Historia 152 · **Enmienda 2** (decisión del PO, 22/09/2026, S15/S24): la PREVISUALIZACIÓN del job
 * que Sun va a copiar, para que el nodo recién soltado no nazca hueco.
 *
 * El job llega del workflow que CONTIENE el nodo de origen (`GET workflow/{sourceWorkflowId}`, ruta
 * existente; §8.4: el `workflowId` de la fila del árbol es ese workflow). Se limpia con la MISMA
 * disciplina que `asDroppedOperationJob` —fuera la identidad de las filas del origen: job, tareas,
 * características, herramientas, preparación y recursos— y además se quitan las dos cosas que
 * `CU-04` paso 4 excluye de la copia: las asignaciones de BOM (`WORKFLOW_RESOURCE`, que apuntan al
 * BOM de OTRO workflow) y el `status` (la ejecución nunca se copia). El `indexValue` es el del
 * origen, que es lo que la copia literal hará (`RN-01.8c-bis`).
 *
 * **Por qué es inocuo mandarla**: el nodo sigue viajando con `intent: 'COPY'` y sus dos parámetros
 * transitorios, y el guardado de Sun DESCARTA el contenido del destino antes de copiar —
 * `PostgresJobGraphRepository#copyWorkflowJobIntoWorkflowJob:179-181` (`deleteChildren(target)` +
 * `documents.deleteJobDocuments` + `copyScalars(source, target)`)—, así que lo previsualizado se
 * sobrescribe con la copia literal y sus adjuntos. La previsualización NO compone la copia: la
 * enseña. Eso es lo que la Enmienda 2 relaja de la letra de `RN-01.8b`.
 *
 * @param {Object} sourceJobReference El job del nodo de origen, tal y como lo devuelve `GET workflow/{id}`
 * @param {Object} [params]
 * @param {Array<{name: string}>} [params.indexValues] Para la degradación al placeholder
 * @param {Array<Object>} [params.progressCalculationTypes] Para la degradación al placeholder
 * @returns {Object} El job con el que nace el nodo, que Sun sobrescribirá al guardar
 */
export const asCopiedJobPreview = (sourceJobReference = {}, { indexValues = [], progressCalculationTypes = [] } = {}) => {
  if (!sourceJobReference || Object.keys(sourceJobReference).length === 0) {
    // Degradación declarada (Enmienda 2 punto 3): sin origen legible, el hueco de siempre.
    return asCopiedJobPlaceholder({ indexValues, progressCalculationTypes })
  }
  const preview = asDroppedOperationJob(sourceJobReference)
  // I15 / CU-04 paso 4: la ejecución nunca se copia, así que tampoco se previsualiza.
  delete preview.status
  // I16 / CU-04 paso 4: las asignaciones de BOM del origen son del BOM de OTRO workflow.
  preview.resources = (preview.resources || []).filter((resource) => resourceTypeOf(resource) !== WORKFLOW_RESOURCE)
  // El lienzo rotula la tarjeta con `technology.name`: un job de origen sin tecnología no la rompe.
  preview.technology = sourceJobReference.technology || {}
  return preview
}

/**
 * Story 167 (PO, 29/09/2026): a local copy is editable before saving. What makes it safe is telling Sun to keep
 * what the node carries and copy only the attachments: which task of the node came from which task of the source
 * (the preview keeps the order of the source's tasks, `asDroppedOperationJob`). Without a readable source the
 * node is the empty placeholder and Sun copies everything, as before.
 *
 * @param {Object} sourceJobReference The source job, as `GET workflow/{id}` returns it
 * @param {Object} copiedJobReference The job the node was born with (`asCopiedJobPreview`)
 * @returns {{ keepEditedContent?: boolean, copiedTasks?: Array<{ taskUuid: string, sourceTaskReferenceId: number }> }} Nothing for the placeholder
 */
export const copyOfEditableContent = (sourceJobReference = {}, copiedJobReference = {}) => {
  const sourceTasks = sourceJobReference?.taskReferences || []
  if (!sourceJobReference || Object.keys(sourceJobReference).length === 0) {
    return {}
  }
  return {
    keepEditedContent: true,
    copiedTasks: (copiedJobReference?.taskReferences || [])
      .map((task, index) => ({ taskUuid: task.uuid, sourceTaskReferenceId: sourceTasks[index]?.id }))
      .filter((pair) => pair.taskUuid && pair.sourceTaskReferenceId)
  }
}

/**
 * Historia 130 (S93, RN-05.9): el job del nodo tras aceptar una actualización a otra versión, para
 * PREVISUALIZARLA antes de guardar.
 *
 * Es el job de la versión destino —sin la identidad de sus filas, como en el arrastre— pero
 * CONSERVANDO lo que es del nodo y no de la operación: los cuatro campos de configuración con sus
 * sellos, el `status` y las asignaciones de BOM del workflow. Exactamente lo mismo que el servidor
 * hará al guardar (RN-05.9), que es lo que hace que la previsualización y el resultado coincidan.
 *
 * @param {Object} currentJobReference El job que el nodo tiene ahora
 * @param {Object} versionJobReference El job de la versión a la que se actualiza (N3)
 * @param {Object} [options] Historia 186 (S165, S166): en CONTROLADO los feature groups se previsualizan con el estado y la
 *   versión que la actualización dejará (RN64), no con los de la operación; en LIBRE (por defecto) siguen siendo su copia.
 * @param {boolean} [options.controlled] Revisiones controladas
 * @param {Object<string, Object>} [options.baselines] Copia persistida de los feature groups por id
 * @param {{ name: string }} [options.draftIndexValue] El índice DRAFT del usuario
 */
export const asUpdatedOperationJob = (currentJobReference = {}, versionJobReference = {}, { controlled = false, baselines = {}, draftIndexValue = { name: 'DRAFT' } } = {}) => {
  const updated = asDroppedOperationJob(versionJobReference)
  TOLERATED_JOB_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(currentJobReference || {}, field)) {
      updated[field] = currentJobReference[field]
    }
  })
  if (controlled) {
    const featureGroups = updatedOperationFeatureGroups({ nodeJob: currentJobReference, operationJob: versionJobReference, baselines, draftIndexValue, newId: getRandomUUID })
    updated.taskReferences = updated.taskReferences.map((task, index) => featureGroups[index] ? { ...task, featureGroups: featureGroups[index] } : task)
  }
  updated.id = currentJobReference?.id ?? null
  updated.status = currentJobReference?.status ?? null
  updated.resources = [
    ...(updated.resources || []),
    ...((currentJobReference?.resources || []).filter((resource) => resourceTypeOf(resource) === WORKFLOW_RESOURCE))
  ]
  return updated
}
